import { useEffect, useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import NotificationBellIcon from '../components/NotificationBellIcon'

interface NotificationItem {
  id: string
  event_id: string
  title: string
  body: string
  priority: 'low' | 'normal' | 'high' | 'urgent'
  data: Record<string, unknown>
  read_at: string | null
  seen_at: string | null
  requires_ack: boolean
  acknowledged_at: string | null
  acknowledged_by: string | null
  created_at: string
}

interface NotificationsResponse {
  notifications?: NotificationItem[]
  unreadCount?: number
  attentionCount?: number
  warning?: string
  error?: string
}

interface NotificationMutationResponse {
  notification?: Partial<NotificationItem>
  notifications?: Partial<NotificationItem>[]
  acknowledgedAt?: string | null
  seenAt?: string | null
  error?: string
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function hrefFromData(data: Record<string, unknown>) {
  return typeof data.href === 'string' && data.href.startsWith('/') ? data.href : null
}

export default function NotificationsPage() {
  const { isLoggedIn, loading } = useAuth()
  const [notifications, setNotifications] = useState<NotificationItem[]>([])
  const [loadingNotifications, setLoadingNotifications] = useState(true)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function loadNotifications() {
    setLoadingNotifications(true)
    setError(null)
    try {
      const res = await fetch('/api/member/notifications', { credentials: 'include' })
      const data = await res.json() as NotificationsResponse
      if (!res.ok) throw new Error(data.error ?? 'Unable to load notifications')
      const items = data.notifications ?? []
      setNotifications(items)
      if (data.warning === 'notification_migration_required') {
        setMessage('Notification tables are not applied yet.')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load notifications')
    } finally {
      setLoadingNotifications(false)
    }
  }

  useEffect(() => {
    if (isLoggedIn) void loadNotifications()
  }, [isLoggedIn])

  async function markRead(id: string) {
    setError(null)
    try {
      const res = await fetch('/api/member/notifications/read', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id }),
      })
      const data = await res.json() as { error?: string }
      if (!res.ok) throw new Error(data.error ?? 'Unable to mark notification read')
      const readAt = new Date().toISOString()
      setNotifications(prev => prev.map(item => item.id === id ? { ...item, read_at: readAt, seen_at: readAt } : item))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to mark notification read')
    }
  }

  async function toggleSeen(item: NotificationItem) {
    const nextSeen = !item.seen_at
    setError(null)
    try {
      const res = await fetch('/api/member/notifications/seen', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: item.id, seen: nextSeen }),
      })
      const data = await res.json() as NotificationMutationResponse
      if (!res.ok) throw new Error(data.error ?? 'Unable to update notification marker')
      const seenAt = nextSeen ? (data.seenAt ?? new Date().toISOString()) : null
      setNotifications(prev => prev.map(nextItem => nextItem.id === item.id ? { ...nextItem, seen_at: seenAt } : nextItem))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update notification marker')
    }
  }

  async function toggleDone(item: NotificationItem) {
    const nextDone = !item.acknowledged_at
    setError(null)
    try {
      const res = await fetch('/api/member/notifications/acknowledge', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ id: item.id, acknowledged: nextDone }),
      })
      const data = await res.json() as NotificationMutationResponse
      if (!res.ok) throw new Error(data.error ?? 'Unable to update notification task')
      const doneAt = nextDone ? (data.acknowledgedAt ?? new Date().toISOString()) : null
      setNotifications(prev => prev.map(nextItem => nextItem.id === item.id ? {
        ...nextItem,
        read_at: nextDone ? (nextItem.read_at ?? doneAt) : nextItem.read_at,
        seen_at: nextDone ? (nextItem.seen_at ?? doneAt) : nextItem.seen_at,
        acknowledged_at: doneAt,
        acknowledged_by: nextDone ? (data.notification?.acknowledged_by ?? nextItem.acknowledged_by) : null,
      } : nextItem))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update notification task')
    }
  }

  async function markAllDone() {
    const hasOpenTasks = notifications.some(item => item.requires_ack && !item.acknowledged_at)
    if (!hasOpenTasks) return
    setError(null)
    setMessage(null)
    try {
      const res = await fetch('/api/member/notifications/acknowledge', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ all: true }),
      })
      const data = await res.json() as NotificationMutationResponse
      if (!res.ok) throw new Error(data.error ?? 'Unable to mark notifications done')
      const doneAt = data.acknowledgedAt ?? new Date().toISOString()
      setNotifications(prev => prev.map(item => item.requires_ack && !item.acknowledged_at ? {
        ...item,
        read_at: item.read_at ?? doneAt,
        seen_at: item.seen_at ?? doneAt,
        acknowledged_at: doneAt,
      } : item))
      setMessage('All open notification tasks marked done.')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to mark notifications done')
    }
  }

  if (loading) {
    return (
      <div className="notifications-page">
        <p className="admin-muted">Checking notifications...</p>
      </div>
    )
  }

  if (!isLoggedIn) return <Navigate to="/signin" replace />

  const unreadCount = notifications.filter(item => !item.seen_at).length
  const attentionCount = notifications.filter(item => item.requires_ack && !item.acknowledged_at).length

  return (
    <main className="notifications-page">
      <header className="notifications-header">
        <div className="notifications-title-block">
          <span className="notifications-icon-mark">
            <NotificationBellIcon />
          </span>
          <div>
            <span className="legal-eyebrow">Member centre</span>
            <h1>Notifications</h1>
            <p>Access requests, role changes, and operational updates that need member attention.</p>
          </div>
        </div>
        <div className="notifications-header-side">
          <div className="notifications-counts">
            <div className="notifications-count">
              <strong>{unreadCount}</strong>
              <span>New</span>
            </div>
            <div className="notifications-count">
              <strong>{attentionCount}</strong>
              <span>To do</span>
            </div>
          </div>
          {attentionCount > 0 && (
            <button className="btn btn-primary" type="button" onClick={() => void markAllDone()}>
              Mark all done
            </button>
          )}
        </div>
      </header>

      {(message || error) && (
        <div className={`admin-alert${error ? ' admin-alert-error' : ''}`} role={error ? 'alert' : 'status'}>
          {error ?? message}
        </div>
      )}

      <section className="notifications-list" aria-label="Notifications list">
        {loadingNotifications ? (
          <div className="notifications-empty">Loading notifications...</div>
        ) : notifications.length === 0 ? (
          <div className="notifications-empty">No notifications yet.</div>
        ) : notifications.map(item => {
          const href = hrefFromData(item.data)
          return (
            <article key={item.id} className={`notification-card${item.seen_at ? ' seen' : ' unread'}${item.requires_ack && !item.acknowledged_at ? ' needs-ack' : ''}${item.acknowledged_at ? ' done' : ''}`}>
              <div className="notification-card-main">
                <div>
                  <span className={`notification-priority notification-priority-${item.priority}`}>{item.priority}</span>
                  {item.requires_ack && !item.acknowledged_at && <span className="notification-ack-label">To do</span>}
                  {item.acknowledged_at && <span className="notification-ack-label acknowledged">Done</span>}
                  <h2>{item.title}</h2>
                  <p>{item.body}</p>
                  <time dateTime={item.created_at}>{formatDate(item.created_at)}</time>
                </div>
                <div className="notification-inline-controls" role="group" aria-label="Notification state controls">
                  <button
                    className={`notification-dot-toggle${item.seen_at ? '' : ' active'}`}
                    type="button"
                    aria-pressed={!item.seen_at}
                    aria-label={item.seen_at ? 'Mark as new' : 'Mark as seen'}
                    onClick={() => void toggleSeen(item)}
                  >
                    <span aria-hidden="true" />
                  </button>
                  {item.requires_ack && (
                    <button
                      className={`notification-done-toggle${item.acknowledged_at ? ' done' : ''}`}
                      type="button"
                      aria-pressed={Boolean(item.acknowledged_at)}
                      aria-label={item.acknowledged_at ? 'Mark as not done' : 'Mark as done'}
                      onClick={() => void toggleDone(item)}
                    >
                      <span aria-hidden="true">✓</span>
                    </button>
                  )}
                </div>
              </div>
              <div className="notification-actions">
                {href && <Link className="btn btn-secondary" to={href}>Open context</Link>}
                {!item.read_at && (
                  <button className={`btn ${item.requires_ack && !item.acknowledged_at ? 'btn-secondary' : 'btn-primary'}`} type="button" onClick={() => void markRead(item.id)}>
                    Mark viewed
                  </button>
                )}
              </div>
            </article>
          )
        })}
      </section>
    </main>
  )
}
